import React, { useRef, useState } from 'react';
import cx from 'classnames';
import UploadFileIcon from '@cp/assets/icons/UploadFileIcon';
import Button from '@primitives/Button';
import Typography from '@primitives/Typography';
import { noop } from '@utils/common';
import { RegisterOptions } from 'react-hook-form';
import { useMergeRefs } from '@floating-ui/react';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';

export type ErrorMessage = {
  fileName: string | null;
  message: string | boolean;
};

type UploadComponentProps = {
  isSampleFileAvailable?: boolean;
  supportedFile?: string;
  MAX_FILE_SIZE_IN_MB?: number;
  validationOfFile?: (file: File | undefined) => boolean;
  file?: File | null;
  setFile: (file: File | null) => void;
  setHasError: (errorMessage: ErrorMessage) => void;
  isValidationInfoVisible?: boolean;
  uploadContentContainer?: (
    handleUploadClick: (e: React.ChangeEvent) => void,
    disabled?: boolean,
    uploadedFileType?: string,
    clickstreamEvent?: { name: string; description: string },
    supportedFile?: string,
    textClassnameContainer?: string
  ) => React.ReactElement;
  containerClassName?: string;
  register?: RegisterOptions;
  refProp?: React.Ref<HTMLInputElement>;
  disabled?: boolean;
  onErrorCallbackFunction?: () => void;
  onSampleFileDownload?: () => void;
  uploadedFileType?: string;
  clickstreamEvent?: { name: string; description: string };
  customClassName?: string;
  textClassnameContainer?: string;
};

function UploadComponent({
  isSampleFileAvailable = false,
  MAX_FILE_SIZE_IN_MB = 5,
  supportedFile = '.csv',
  validationOfFile,
  file: uploadedFile = null,
  setFile: setUploadedFile,
  setHasError = (errorMessage: ErrorMessage) => noop,
  isValidationInfoVisible = false,
  uploadContentContainer,
  containerClassName = '',
  refProp,
  disabled = false,
  onErrorCallbackFunction,
  onSampleFileDownload,
  uploadedFileType,
  clickstreamEvent,
  customClassName,
  textClassnameContainer,
  ...rest
}: UploadComponentProps) {
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const mergedRef = useMergeRefs([hiddenFileInput, refProp]);

  const defaultValidationOfFile = (file: File | undefined) => {
    if (file) {
      if (file?.size > MAX_FILE_SIZE) {
        return `File size is greater than ${MAX_FILE_SIZE_IN_MB} MB`;
      }
    }
    return false;
  };

  const handleFile = (file: File | undefined) => {
    if (file) {
      setUploadedFile(file);
      setHasError({
        fileName: '',
        message: ''
      });
      const validationFunc = validationOfFile || defaultValidationOfFile;
      const fileValidationMessage = validationFunc(file);
      if (!fileValidationMessage) {
        setUploadedFile(file);
      } else {
        setHasError({
          fileName: file.name,
          message: fileValidationMessage
        });
        setUploadedFile(null);
        onErrorCallbackFunction && onErrorCallbackFunction();
      }
    }
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    handleFile(selectedFile);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const selectedFile = event.dataTransfer.files?.[0];
    handleFile(selectedFile);
  };
  const MAX_FILE_SIZE = 1e6 * MAX_FILE_SIZE_IN_MB;

  const handleUploadClick = (e: React.ChangeEvent<any>) => {
    if (clickstreamEvent) addClickstreamEvent(clickstreamEvent);
    e.preventDefault();
    hiddenFileInput.current?.focus();
    hiddenFileInput.current?.click();
  };

  const handleDownloadClick = (e: React.ChangeEvent<any>) => {
    e.preventDefault();
    onSampleFileDownload && onSampleFileDownload();
  };
  return (
    <div className="">
      <div className="">
        <div
          className={cx(
            {
              'border-dashed border-[2px] border-[--blue-base] bg-[--blue-light-bg]  p-[24px] m-[12px] flex flex-col  items-center justify-space-evenly rounded-lg':
                true,
              ['!bg-[--blue-bg] border-1 border-solid border-[--blue-bg]']:
                isDragging && !uploadedFile,
              ['cursor-not-allowed']: disabled
            },
            containerClassName
          )}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
        >
          <input
            id="fileUpload"
            type="file"
            accept={supportedFile}
            onChange={handleChange}
            onClick={(e: React.ChangeEvent<any>) => {
              e.target.value = '';
            }}
            ref={hiddenFileInput}
            style={{ display: 'none' }}
          />
          {uploadContentContainer ? (
            uploadContentContainer(
              handleUploadClick,
              disabled,
              uploadedFileType,
              clickstreamEvent,
              supportedFile,
              textClassnameContainer
            )
          ) : (
            <div className="upload-container-content flex flex-col items-center justify-center">
              <UploadFileIcon />
              <div className={'text-[--grayscale-content-2]'}>
                <Button variant="text" onClick={handleUploadClick}>
                  Click to Select
                </Button>{' '}
                or drag and drop here
              </div>
            </div>
          )}
          {isValidationInfoVisible ? (
            <div className={'flex items-center gap-2 '}>
              <Typography variant={'p5'} className="text-[--grayscale-3] text-center pt-2 pb-2">
                Upload only {supportedFile?.toString() || '.csv'}
              </Typography>
              <div className="w-[1px] h-[20px] bg-[#caced5] mt-[4px]" />
              <Typography variant={'p5'} className="text-[--grayscale-3] text-center pt-2 pb-2">
                Max {MAX_FILE_SIZE_IN_MB ? MAX_FILE_SIZE_IN_MB : 5} MB size allowed
              </Typography>
            </div>
          ) : null}
          {isSampleFileAvailable ? (
            <Button
              onClick={handleDownloadClick}
              variant="text"
              className={cx('text-center text-[--blue-base]', customClassName)}
            >
              {' '}
              Download Sample file
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default UploadComponent;
